import { DateTime } from 'luxon'

export default class Comission {
  /**
   * Cálculo por item — base única para os totais e para as linhas detalhadas
   * das telas, do PDF e da planilha.
   *
   * Regras de negócio (ver Comission.test.js):
   *  1. Serviço debitado de pacote ou de cartão fidelidade não entra na receita
   *     bruta do período: o salão faturou na venda do pacote / no serviço que
   *     gerou o carimbo. Ele ainda gera comissão para quem executou, então a
   *     soma "comissões + líquido do salão" fica acima da receita bruta.
   *  2. Caixinha também fica fora da receita bruta, e é rateada entre salão e
   *     profissional conforme a comissão configurada.
   *  3. Em um débito de pacote, comission_type é o snapshot gravado no momento
   *     do débito (API: Customer.subtractService). Editar o pacote depois não
   *     altera comissões já fechadas.
   */
  static isBilledItem(item) {
    return (
      item.type === 'cutilei' ||
      (!item.bundle_id && !item.loyalty_card_id && !item.cutilei_schedule)
    )
  }

  static getItemPrice(item) {
    return item.custom_price ?? item.price ?? item.value ?? 0
  }

  /**
   * Valor que o cliente pagou por este item. Um serviço debitado de pacote já
   * foi pago na venda do pacote: discount_price é a fatia dele, rateada pelo
   * desconto que o cliente teve (API: Customer.subtractService). Cartão
   * fidelidade vale 0.
   *
   * @param {Object} item - Serviço, agendamento ou item de comanda.
   * @returns {number} Valor pago pelo cliente por este item especificamente.
   */
  static getItemPaidPrice(item) {
    if (item.bundle_id || item.loyalty_card_id) return item.discount_price ?? 0
    return this.getItemPrice(item)
  }

  static getItemComissionPercentage(item) {
    return (
      item.comission_percentage ??
      (item.professional_id ? item.custom_comission_percentage : 0)
    )
  }

  // Só o que foi faturado: a caixinha tem apenas `value` e fica de fora, ao
  // contrário do rateio salão/profissional, que usa getItemPrice.
  static getItemTotalEarnings(item) {
    return item.custom_price ?? item.price ?? 0
  }

  static getItemBusinessEarnings(item) {
    return this.getItemPrice(item) * (1 - this.getItemComissionPercentage(item))
  }

  // Percentual efetivamente aplicado ao item, para exibição: um pacote
  // no_comission zera a comissão mesmo com percentual configurado.
  static getItemComissionRate(item) {
    if (
      item.bundle_id &&
      (item.comission_type ?? item.bundle?.comission_type) === 'no_comission'
    )
      return 0

    return this.getItemComissionPercentage(item)
  }

  static getItemProfessionalEarnings(item) {
    const comissionPercentage = this.getItemComissionPercentage(item)

    if (item.bundle_id) {
      // item.bundle é fallback para débitos gravados antes do snapshot existir.
      const comissionType = item.comission_type ?? item.bundle?.comission_type

      if (comissionType === 'no_comission') return 0
      if (comissionType === 'bundle_value')
        return item.discount_price * comissionPercentage
    }

    return this.getItemPrice(item) * comissionPercentage
  }

  // Caixinha vem com description '' — evita linha sem nome no PDF e na planilha.
  static getItemLabel(item) {
    return item.name || item.service?.name || item.description || 'Caixinha'
  }

  static getTotalEarnings(
    earnings,
    type,
    category = null,
    professional = null,
  ) {
    const cutileiEarnings = earnings.filter(
      (earning) => earning.type === 'cutilei',
    )
    const billEarnings = earnings.filter((earning) => earning.type === 'bill')
    let cutileiEarningsTotal = 0
    let billEarningsTotal = 0
    const sumItemTotal = (total, item) =>
      total + this.getItemTotalEarnings(item)

    if (['cutilei', 'all_earnings'].includes(type.value))
      cutileiEarningsTotal += cutileiEarnings
        .filter((schedule) =>
          this.filterProfessionalEarnings(schedule, professional),
        )
        .filter((schedule) =>
          category ? schedule.service.category.id === category?.value : true,
        )
        .reduce(sumItemTotal, 0)

    if (['bill', 'all_earnings'].includes(type.value)) {
      const scheduleEarningsTotal = billEarnings.reduce(
        (total, bill) =>
          total +
          bill.schedules
            .filter((schedule) => this.isBilledItem(schedule))
            .filter((schedule) =>
              this.filterProfessionalEarnings(schedule, professional),
            )
            .filter((schedule) =>
              category
                ? schedule.service.category.id === category?.value
                : true,
            )
            .reduce(sumItemTotal, 0),
        0,
      )

      const serviceEarningsTotal = billEarnings.reduce(
        (total, bill) =>
          total +
          bill.services
            .filter((service) => this.isBilledItem(service))
            .filter((service) =>
              this.filterProfessionalEarnings(service, professional),
            )
            .filter((service) =>
              category ? service.category.id === category?.value : true,
            )
            .reduce(sumItemTotal, 0),
        0,
      )

      const productEarningsTotal = category
        ? 0
        : billEarnings.reduce(
            (total, bill) =>
              total +
              bill.products
                .filter((product) =>
                  this.filterProfessionalEarnings(product, professional),
                )
                .reduce(sumItemTotal, 0),
            0,
          )

      const bundleEarningsTotal = category
        ? 0
        : billEarnings.reduce(
            (total, bill) =>
              total +
              bill.bundles
                .filter((bundle) =>
                  this.filterProfessionalEarnings(bundle, professional),
                )
                .reduce(sumItemTotal, 0),
            0,
          )

      billEarningsTotal =
        scheduleEarningsTotal +
        serviceEarningsTotal +
        productEarningsTotal +
        bundleEarningsTotal
    }

    return cutileiEarningsTotal + billEarningsTotal
  }

  static getBusinessEarnings(
    earnings,
    type,
    category = null,
    professional = null,
  ) {
    const cutileiEarnings = earnings.filter(
      (earning) => earning.type === 'cutilei',
    )
    const billEarnings = earnings.filter((earning) => earning.type === 'bill')
    let cutileiEarningsTotal = 0
    let billEarningsTotal = 0

    const sumBusinessItemsTotal = (total, item) =>
      total + this.getItemBusinessEarnings(item)

    if (['cutilei', 'all_earnings'].includes(type.value))
      cutileiEarningsTotal += cutileiEarnings
        .filter((schedule) =>
          this.filterProfessionalEarnings(schedule, professional),
        )
        .filter((schedule) =>
          category ? schedule.service.category.id === category?.value : true,
        )
        .reduce(sumBusinessItemsTotal, 0)

    if (['bill', 'all_earnings'].includes(type.value)) {
      const scheduleEarningsTotal = billEarnings.reduce(
        (total, bill) =>
          total +
          bill.schedules
            .filter((schedule) => this.isBilledItem(schedule))
            .filter((schedule) =>
              this.filterProfessionalEarnings(schedule, professional),
            )
            .filter((schedule) =>
              category
                ? schedule.service.category.id === category?.value
                : true,
            )
            .reduce(sumBusinessItemsTotal, 0),
        0,
      )

      const serviceEarningsTotal = billEarnings.reduce(
        (total, bill) =>
          total +
          bill.services
            .filter((service) => this.isBilledItem(service))
            .filter((service) =>
              this.filterProfessionalEarnings(service, professional),
            )
            .filter((service) =>
              category ? service.category.id === category?.value : true,
            )
            .reduce(sumBusinessItemsTotal, 0),
        0,
      )

      const productEarningsTotal = category
        ? 0
        : billEarnings.reduce(
            (total, bill) =>
              total +
              bill.products
                .filter((product) =>
                  this.filterProfessionalEarnings(product, professional),
                )
                .reduce(sumBusinessItemsTotal, 0),
            0,
          )

      const bundleEarningsTotal = category
        ? 0
        : billEarnings.reduce(
            (total, bill) =>
              total +
              bill.bundles
                .filter((bundle) =>
                  this.filterProfessionalEarnings(bundle, professional),
                )
                .reduce(sumBusinessItemsTotal, 0),
            0,
          )

      const tipEarningsTotal = category
        ? 0
        : billEarnings.reduce(
            (total, bill) =>
              total +
              bill.tips
                .filter((tip) =>
                  this.filterProfessionalEarnings(tip, professional),
                )
                .reduce(sumBusinessItemsTotal, 0),
            0,
          )

      billEarningsTotal =
        scheduleEarningsTotal +
        serviceEarningsTotal +
        productEarningsTotal +
        bundleEarningsTotal +
        tipEarningsTotal
    }

    return cutileiEarningsTotal + billEarningsTotal
  }

  static getProfessionalEarnings(
    earnings,
    type,
    category = null,
    professional = null,
  ) {
    const cutileiEarnings = earnings.filter(
      (earning) => earning.type === 'cutilei',
    )
    const billEarnings = earnings.filter((earning) => earning.type === 'bill')
    let cutileiEarningsTotal = 0
    let billEarningsTotal = 0

    // Serviço de pacote / cartão fidelidade entra aqui mesmo ficando fora da
    // receita bruta: quem executou recebe comissão.
    const sumProfessionalItemsTotal = (total, item) =>
      total + this.getItemProfessionalEarnings(item)

    if (['cutilei', 'all_earnings'].includes(type.value))
      cutileiEarningsTotal += cutileiEarnings
        .filter((schedule) =>
          this.filterProfessionalEarnings(schedule, professional),
        )
        .filter((schedule) =>
          category ? schedule.service.category.id === category?.value : true,
        )
        .reduce(sumProfessionalItemsTotal, 0)

    if (['bill', 'all_earnings'].includes(type.value)) {
      const scheduleEarningsTotal = billEarnings.reduce(
        (total, bill) =>
          total +
          bill.schedules
            .filter((s) => !s.cutilei_schedule)
            .filter((schedule) =>
              this.filterProfessionalEarnings(schedule, professional),
            )
            .filter((schedule) =>
              category
                ? schedule.service.category.id === category?.value
                : true,
            )
            .reduce(sumProfessionalItemsTotal, 0),
        0,
      )

      const serviceEarningsTotal = billEarnings.reduce(
        (total, bill) =>
          total +
          bill.services
            .filter((service) =>
              this.filterProfessionalEarnings(service, professional),
            )
            .filter((service) =>
              category ? service.category.id === category?.value : true,
            )
            .reduce(sumProfessionalItemsTotal, 0),
        0,
      )

      const productEarningsTotal = category
        ? 0
        : billEarnings.reduce(
            (total, bill) =>
              total +
              bill.products
                .filter((product) =>
                  this.filterProfessionalEarnings(product, professional),
                )
                .reduce(sumProfessionalItemsTotal, 0),
            0,
          )

      const bundleEarningsTotal = category
        ? 0
        : billEarnings.reduce(
            (total, bill) =>
              total +
              bill.bundles
                .filter((bundle) =>
                  this.filterProfessionalEarnings(bundle, professional),
                )
                .reduce(sumProfessionalItemsTotal, 0),
            0,
          )

      const tipEarningsTotal = category
        ? 0
        : billEarnings.reduce(
            (total, bill) =>
              total +
              bill.tips
                .filter((tip) =>
                  this.filterProfessionalEarnings(tip, professional),
                )
                .reduce(sumProfessionalItemsTotal, 0),
            0,
          )

      billEarningsTotal =
        scheduleEarningsTotal +
        serviceEarningsTotal +
        productEarningsTotal +
        bundleEarningsTotal +
        tipEarningsTotal
    }

    return cutileiEarningsTotal + billEarningsTotal
  }

  static getItemCategoryId(item) {
    return item.service?.category?.id ?? item.category?.id
  }

  /**
   * Recibo de pagamento de comissões: uma linha por serviço/agendamento
   * executado pelo profissional filtrado — pacote e fidelidade entram como
   * qualquer outro item, com o valor pago pelo cliente naquele serviço (ver
   * getItemPaidPrice) e o percentual configurado (getItemComissionRate, que
   * já zera pacote no_comission). Filtra por profissional aqui mesmo que a
   * API já tenha filtrado: quem não tem financial.comissions gera o próprio
   * relatório sem professional_id, e a API devolve a comanda inteira.
   * Produtos vendidos entram como linha, exceto com filtro de categoria de
   * serviço ativo (como nos totais). Cada linha leva `kind` ('service' ou
   * 'product'), e o PDF lista os produtos numa tabela própria, abaixo da de
   * serviços. Os dois seguem num array só para que uma API sem essa
   * separação continue somando os produtos no total do recibo.
   * Item com quantidade maior que 1 leva " (2x)" na descrição, porque o
   * valor da linha é o total dela.
   * `totals.comissions` é o somatório dessas
   * linhas — pacote (venda) e caixinha não entram no recibo, mesmo contando
   * para getProfessionalEarnings, então os dois valores podem divergir.
   *
   * @param {Array<Object>} earnings - Ganhos do período, com `type` em
   *   `'bill' | 'cutilei' | 'bonus' | 'discount'`.
   * @param {{ value: string }} type - Tipo de ganho selecionado no filtro
   *   (`'bill' | 'cutilei' | 'all_earnings'`).
   * @param {{ value: number }|null} [category] - Categoria selecionada no
   *   filtro, ou `null` para não filtrar por categoria.
   * @param {{ id: number }|null} [professional] - Profissional selecionado
   *   no filtro, ou `null` para não filtrar por profissional.
   * @returns {{
   *   comissions: Array<{ date: string, customer: string, description: string, value: number, rate: number, comission: number, kind: 'service' | 'product' }>,
   *   bonuses: Array<{ date: string, value: number, description: string }>,
   *   discounts: Array<{ date: string, value: number, description: string }>,
   *   totals: { comissions: number, bonuses: number, discounts: number, total: number },
   * }} Recibo pronto para o PDF de pagamento de comissões.
   */
  static getComissionPaymentReport(
    earnings,
    type,
    category = null,
    professional = null,
  ) {
    const {
      bill: billEarnings,
      cutilei: cutileiEarnings,
      bonus: bonusEarnings,
      discount: discountEarnings,
    } = earnings.reduce(
      (acc, earning) => {
        acc[earning.type]?.push(earning)
        return acc
      },
      { bill: [], cutilei: [], bonus: [], discount: [] },
    )

    const matchesCategory = (item) =>
      category ? this.getItemCategoryId(item) === category.value : true

    const passesFilters = (item) =>
      this.filterProfessionalEarnings(item, professional) &&
      matchesCategory(item)

    const byDate = (a, b) =>
      DateTime.fromISO(a.date).toMillis() -
        DateTime.fromISO(b.date).toMillis() ||
      (a.customer < b.customer ? -1 : a.customer > b.customer ? 1 : 0) ||
      (a.description < b.description
        ? -1
        : a.description > b.description
          ? 1
          : 0)

    const includesType = (value) => ['all_earnings', value].includes(type.value)

    const originSuffix = (item) =>
      item.bundle_id ? ' (Pacote)' : item.loyalty_card_id ? ' (Fidelidade)' : ''

    // O valor da linha já é o total da quantidade (custom_price), então sem ela
    // "Esmalte — R$ 16,00" parece o preço de uma unidade só.
    const quantitySuffix = (item) =>
      item.amount > 1 ? ` (${item.amount}x)` : ''

    const buildRow = (date, customerName, item, kind, suffix = '') => ({
      date,
      customer: customerName ?? '',
      description: `${this.getItemLabel(item)}${quantitySuffix(item)}${suffix}${originSuffix(item)}`,
      value: this.getItemPaidPrice(item),
      rate: this.getItemComissionRate(item),
      comission: this.getItemProfessionalEarnings(item),
      kind,
    })

    const buildBillRows = (bill) => {
      const toRows = (items, kind) =>
        items
          .filter(passesFilters)
          .map((item) =>
            buildRow(item.date ?? bill.date, bill.customer?.name, item, kind),
          )

      return [
        ...toRows(
          [
            ...bill.schedules.filter((schedule) => !schedule.cutilei_schedule),
            ...bill.services,
          ],
          'service',
        ),
        ...toRows(category ? [] : (bill.products ?? []), 'product'),
      ]
    }

    const buildCutileiRow = (schedule) =>
      passesFilters(schedule)
        ? [
            buildRow(
              schedule.date,
              schedule.member?.name ?? schedule.customer?.name,
              schedule,
              'service',
              ' (Cutilei)',
            ),
          ]
        : []

    const comissions = [
      ...(includesType('bill') ? billEarnings.flatMap(buildBillRows) : []),
      ...(includesType('cutilei')
        ? cutileiEarnings.flatMap(buildCutileiRow)
        : []),
    ].sort(byDate)

    const bonuses = bonusEarnings
      .map((bonus) => ({
        date: bonus.date,
        value: bonus.value,
        description: bonus.description,
      }))
      .sort(byDate)

    const discounts = discountEarnings
      .map((discount) => ({
        date: discount.date,
        value: Math.abs(discount.value),
        description: discount.description,
      }))
      .sort(byDate)

    const comissionsTotal = comissions.reduce(
      (total, row) => total + row.comission,
      0,
    )
    const bonusTotal = bonuses.reduce((total, bonus) => total + bonus.value, 0)
    const discountTotal = discounts.reduce(
      (total, discount) => total + discount.value,
      0,
    )

    return {
      comissions,
      bonuses,
      discounts,
      totals: {
        comissions: comissionsTotal,
        bonuses: bonusTotal,
        discounts: discountTotal,
        total: comissionsTotal + bonusTotal - discountTotal,
      },
    }
  }

  /**
   * Comissões de serviços debitados de pacote ou de cartão fidelidade: geram
   * comissão sem entrar na receita bruta do período.
   */
  static getUnbilledComissions(
    earnings,
    type,
    category = null,
    professional = null,
  ) {
    const empty = { qtd: 0, value: 0 }

    if (!['bill', 'all_earnings'].includes(type.value)) return empty

    return earnings
      .filter((earning) => earning.type === 'bill')
      .reduce((total, bill) => {
        const items = [
          ...bill.schedules
            .filter((schedule) => !schedule.cutilei_schedule)
            .filter((schedule) =>
              category
                ? schedule.service.category.id === category?.value
                : true,
            ),
          ...bill.services.filter((service) =>
            category ? service.category.id === category?.value : true,
          ),
        ]
          .filter((item) => !this.isBilledItem(item))
          .filter((item) => this.filterProfessionalEarnings(item, professional))

        return {
          qtd:
            total.qtd +
            items.reduce((qtd, item) => qtd + (item.amount || 1), 0),
          value:
            total.value +
            items.reduce(
              (value, item) => value + this.getItemProfessionalEarnings(item),
              0,
            ),
        }
      }, empty)
  }

  /**
   * Caixinhas: ficam fora da receita bruta e são rateadas entre salão e
   * profissional. Com filtro de categoria de serviço ativo elas saem de todos
   * os cálculos, como produtos e pacotes.
   */
  static getTipComissions(
    earnings,
    type,
    category = null,
    professional = null,
  ) {
    const empty = { qtd: 0, professionalValue: 0, businessValue: 0 }

    if (category || !['bill', 'all_earnings'].includes(type.value)) return empty

    return earnings
      .filter((earning) => earning.type === 'bill')
      .reduce((total, bill) => {
        const tips = bill.tips.filter((tip) =>
          this.filterProfessionalEarnings(tip, professional),
        )

        return {
          qtd:
            total.qtd + tips.reduce((qtd, tip) => qtd + (tip.amount || 1), 0),
          professionalValue:
            total.professionalValue +
            tips.reduce(
              (value, tip) => value + this.getItemProfessionalEarnings(tip),
              0,
            ),
          businessValue:
            total.businessValue +
            tips.reduce(
              (value, tip) => value + this.getItemBusinessEarnings(tip),
              0,
            ),
        }
      }, empty)
  }

  /**
   * Explica a diferença entre "comissões + líquido do salão" e a receita bruta
   * do período. Sobre o que foi faturado no período vale a identidade
   * `totalEarnings === billedComissions + businessBilledEarnings`; o que sobra
   * são as comissões de pacote/fidelidade e as caixinhas.
   */
  static getReconciliation(
    earnings,
    type,
    category = null,
    professional = null,
  ) {
    const totalEarnings = this.getTotalEarnings(
      earnings,
      type,
      category,
      professional,
    )
    const professionalEarnings = this.getProfessionalEarnings(
      earnings,
      type,
      category,
      professional,
    )
    const bundleComissions = this.getUnbilledComissions(
      earnings,
      type,
      category,
      professional,
    )
    const tipComissions = this.getTipComissions(
      earnings,
      type,
      category,
      professional,
    )

    const billedComissions =
      professionalEarnings -
      bundleComissions.value -
      tipComissions.professionalValue

    return {
      totalEarnings,
      billedComissions,
      // Derivado do bruto para a tabela sempre fechar. Equivale a
      // getBusinessEarnings menos a parte do salão nas caixinhas.
      businessBilledEarnings: totalEarnings - billedComissions,
      bundleComissions,
      tipComissions,
      professionalEarnings,
    }
  }

  static filterProfessionalEarnings(item, professional) {
    if (professional)
      return [item.professional_id, item.professional?.id].includes(
        professional.id,
      )
    return true
  }

  static getTotalBillEarnings(bills) {
    if (bills.length === 0) return 0

    return bills
      .map((bill) => bill.value)
      .reduce((total, value) => total + value)
  }

  static getBusinessBillEarnings(bills) {
    if (bills.length === 0) return 0

    return bills
      .map((bill) => bill.price * (1 - bill.comission_percentage))
      .reduce((total, value) => total + value)
  }

  static getProfessionalBillEarnings(bills) {
    if (bills.length === 0) return 0

    return bills
      .map((bill) => bill.price * bill.comission_percentage)
      .reduce((total, value) => total + value)
  }

  static getEarningTypeLabel(type) {
    switch (type) {
      case 'cutilei':
        return 'Agendamentos Cutilei'
      case 'services':
        return 'Serviços'
      case 'products':
        return 'Produtos'
      case 'bundles':
        return 'Pacotes'
      case 'tips':
        return 'Caixinhas'
      case 'bonuses':
        return 'Bônus'
      case 'discounts':
        return 'Descontos'
      default:
        return type
    }
  }
}
